// <Table<T>> — generic data table per DESIGN.md §7.3.
//
// Row height 40px (body), header 32px (h3 eyebrow, uppercase).
// Horizontal rules only (no vertical). Shimmer-skeleton loading (3 rows).
// Centered bodyLg empty state.

import type { ReactNode } from "react";
import "./Table.css";

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  /** Render a cell for this row; return ReactNode. */
  render: (row: T, index: number) => ReactNode;
  /** Optional width (CSS value, e.g. "120px" or "1fr"). */
  width?: string;
  /** Optional alignment for the cell contents. */
  align?: "left" | "right" | "center";
}

export interface TableProps<T> {
  columns: readonly TableColumn<T>[];
  rows: readonly T[];
  emptyState?: ReactNode;
  loading?: boolean;
  /** Stable key for each row; defaults to array index (avoid when rows reorder). */
  rowKey?: (row: T, index: number) => string;
  /** Optional per-row click handler; renders rows as buttons when provided. */
  onRowClick?: (row: T, index: number) => void;
}

export function Table<T>(props: TableProps<T>): JSX.Element {
  const {
    columns,
    rows,
    emptyState = "No rows.",
    loading = false,
    rowKey,
    onRowClick,
  } = props;

  return (
    <div className="rb-table-wrap">
      <table className="rb-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={{
                  width: col.width,
                  textAlign: col.align ?? "left",
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="rb-table__row--skeleton">
                  {columns.map((col) => (
                    <td key={col.key}>
                      <span className="rb-table__skeleton-cell" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.length === 0
              ? null
              : rows.map((row, index) => {
                  const key = rowKey ? rowKey(row, index) : String(index);
                  const clickable = onRowClick !== undefined;
                  return (
                    <tr
                      key={key}
                      className={clickable ? "rb-table__row--clickable" : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onClick={
                        clickable ? () => onRowClick(row, index) : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onRowClick(row, index);
                              }
                            }
                          : undefined
                      }
                    >
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          style={{ textAlign: col.align ?? "left" }}
                        >
                          {col.render(row, index)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
        </tbody>
      </table>
      {!loading && rows.length === 0 && (
        <div className="rb-table__empty" role="status">
          {emptyState}
        </div>
      )}
    </div>
  );
}
